import { useEffect, useState } from "react";
import { Globe2, X } from "lucide-react";
import { useLocation } from "wouter";
import { localePath, LOCALE_LABELS, availableLocalesForPath, type Locale } from "@/lib/locale";

const PREFERENCE_KEY = "ovvo-language-preference";
const DISMISSED_KEY = "ovvo-language-suggestion-dismissed";

const COPY: Record<Exclude<Locale, "it">, { message: string; accept: string; dismiss: string }> = {
  en: {
    message: "Would you like to view this website in English?",
    accept: "View in English",
    dismiss: "Continue in Italian",
  },
  de: {
    message: "Möchten Sie diese Website auf Deutsch ansehen?",
    accept: "Auf Deutsch ansehen",
    dismiss: "Auf Italienisch fortfahren",
  },
  hr: {
    message: "Želite li ovu web-stranicu pregledavati na hrvatskom?",
    accept: "Prikaži na hrvatskom",
    dismiss: "Nastavi na talijanskom",
  },
};

function browserLocale(): Exclude<Locale, "it"> | null {
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const language of languages) {
    const code = language.toLowerCase().split("-")[0];
    if (code === "en" || code === "de" || code === "hr") return code;
  }
  return null;
}

export function rememberLanguagePreference(locale: Locale) {
  try {
    localStorage.setItem(PREFERENCE_KEY, locale);
    localStorage.removeItem(DISMISSED_KEY);
  } catch {
    // La navigazione deve funzionare anche se lo storage è disabilitato.
  }
}

export function LanguageSuggestion() {
  const [location] = useLocation();
  const [suggestedLocale, setSuggestedLocale] = useState<Exclude<Locale, "it"> | null>(null);

  useEffect(() => {
    try {
      if (localStorage.getItem(PREFERENCE_KEY) || localStorage.getItem(DISMISSED_KEY)) return;
    } catch {
      return;
    }

    const firstSegment = window.location.pathname.split("/").filter(Boolean)[0];
    if (firstSegment === "en" || firstSegment === "de" || firstSegment === "hr") return;

    setSuggestedLocale(browserLocale());
  }, []);

  if (!suggestedLocale || !availableLocalesForPath(location).includes(suggestedLocale)) return null;

  const copy = COPY[suggestedLocale];

  const accept = () => {
    rememberLanguagePreference(suggestedLocale);
    const destination = localePath(window.location.pathname, suggestedLocale);
    window.location.assign(`${destination}${window.location.search}${window.location.hash}`);
  };

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, "it");
    } catch {
      // L'avviso può comunque essere chiuso per la sessione corrente.
    }
    setSuggestedLocale(null);
  };

  return (
    <aside
      className="fixed inset-x-3 top-[84px] z-40 mx-auto flex max-w-2xl items-center gap-3 rounded-2xl border border-border bg-background/95 p-3 shadow-lg backdrop-blur-md md:top-[96px] md:p-4"
      aria-label={`Language suggestion: ${LOCALE_LABELS[suggestedLocale]}`}
    >
      <span className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary sm:flex">
        <Globe2 className="h-5 w-5" aria-hidden="true" />
      </span>
      <p className="min-w-0 flex-1 text-sm font-medium text-foreground">{copy.message}</p>
      <button
        type="button"
        onClick={accept}
        className="shrink-0 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        {copy.accept}
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label={copy.dismiss}
        title={copy.dismiss}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </aside>
  );
}